import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { CodeBlock } from "@freightsimple/ui-core/Display/CodeBlock";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { WebhookDeliveryState } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDeliveryState";
import type { WebhookDelivery } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDelivery";
import type { WebhookDeliveryDetail } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDeliveryDetail";
import {
  describeWebhookDeliveryTrigger,
  describeWebhookEventType,
} from "../Helpers/webhooksLabels";
import { webhooksBodyForDisplay } from "../Helpers/webhooksRetryEligibility";
import {
  webhooksOutcomeBadge,
  webhooksResponseCell,
  webhooksWhenCell,
} from "../Helpers/webhooksDeliveryCells";

// The event's history at this endpoint: the original send and every redelivery, oldest first.
const attemptColumns: Column<WebhookDelivery>[] = [
  {
    key: "type",
    header: "Type",
    render: (d) => (
      <T size="xs" weight="medium">
        {describeWebhookDeliveryTrigger(d.trigger)}
        {d.triggeredByEmail !== undefined && (
          <T size="xs" color="muted">
            {" "}
            by {d.triggeredByEmail}
          </T>
        )}
      </T>
    ),
  },
  { key: "outcome", header: "Outcome", render: webhooksOutcomeBadge },
  { key: "response", header: "Response", render: webhooksResponseCell },
  {
    key: "httpAttempts",
    header: "HTTP attempts",
    align: "right",
    render: (d) => (
      <T size="xs" color="muted">
        {d.attemptCount}
      </T>
    ),
  },
  { key: "when", header: "Sent", render: webhooksWhenCell },
];

interface WebhooksDeliveryDialogProps extends DialogBaseProps {
  /** Undefined while the bodies are still loading. */
  detail: WebhookDeliveryDetail | undefined;
}

/**
 * The exact JSON FreightSimple sent to the customer's endpoint, what it answered, and every
 * other time the same event was sent there. This is the customer's own debugging view: when
 * their handler rejects an event, the request shows what we signed and the response shows what
 * their server said about it.
 */
export function WebhooksDeliveryDialog({
  open,
  onOpenChange,
  detail,
}: WebhooksDeliveryDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Webhook delivery"
      description={
        detail
          ? `${describeWebhookEventType(detail.delivery.eventType)} to ${detail.delivery.webhookUrl}`
          : undefined
      }
      width="wide"
    >
      <ControlPresentationProvider presentation="underlined">
        {detail === undefined ? (
          <Spinner />
        ) : (
          <Stack gap={5}>
            <Stack gap={2}>
              <Heading level={3}>What we sent</Heading>
              <CodeBlock language="json">
                {webhooksBodyForDisplay(detail.requestBody).text}
              </CodeBlock>
            </Stack>
            <Stack gap={2}>
              <Heading level={3}>
                What your endpoint answered{" "}
                {detail.delivery.httpStatus !== undefined && (
                  <Badge
                    label={`HTTP ${detail.delivery.httpStatus}`}
                    variant={
                      detail.delivery.state === WebhookDeliveryState.Delivered
                        ? "success"
                        : "danger"
                    }
                  />
                )}
              </Heading>
              {detail.responseBody ? (
                <CodeBlock
                  language={
                    webhooksBodyForDisplay(detail.responseBody).language
                  }
                >
                  {webhooksBodyForDisplay(detail.responseBody).text}
                </CodeBlock>
              ) : (
                <T color="secondary">
                  {detail.delivery.errorMessage ??
                    "No response body was returned."}
                </T>
              )}
            </Stack>
            <Stack gap={2}>
              <Heading level={3}>Attempts</Heading>
              <T color="secondary" size="sm">
                Every time this event was sent to this endpoint. The highlighted
                row is the one you opened; a delivery can be re-sent up to{" "}
                {detail.maxCustomerRetries} times from the list.
              </T>
              <Table
                tableId="webhook-delivery-attempts"
                columns={attemptColumns}
                data={detail.attempts}
                rowKey={(d) => d.companyWebhookDeliveryId}
                rowHighlight={(d) =>
                  d.companyWebhookDeliveryId ===
                  detail.delivery.companyWebhookDeliveryId
                    ? "blue"
                    : undefined
                }
              />
            </Stack>
          </Stack>
        )}
      </ControlPresentationProvider>
    </Dialog>
  );
}
